import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import { Grid, Divider } from "@mui/material";

import "./index.css";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import SignInLayout from "layouts/authentication/components/SignInLayout";
import { verifyEmail, resendVerificationCode } from "../../../api/authApi";
import { LanguageContext } from "../../../context/LanguageContext";
import { COLORS, TYPO } from "theme/tokens";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const storedEmail = localStorage.getItem("pendingVerifyEmail");
  const email = params.get("email") || storedEmail;

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("verify_email", "authentication/verify-email/verify-email_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.verify_email || { Main: {}, Alert: {} },
    [componentLanguageData]
  );

  useEffect(() => {
    if (loading) document.body.classList.add("loading");
    else document.body.classList.remove("loading");
    return () => document.body.classList.remove("loading");
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let message_code;
    try {
      const response = await verifyEmail({ email, verificationCode: code });
      if (response.data.message === "Success_verify_001") message_code = LANGUAGE.Alert.Content1;
      toast.success(message_code);
      localStorage.removeItem("pendingVerifyEmail");
      navigate("/authentication/sign-in");
    } catch (error) {
      const m = error.response?.data?.message;
      if (m === "Error_verify_001") message_code = LANGUAGE.Alert.Content2;
      if (m === "Error_verify_002") message_code = LANGUAGE.Alert.Content3;
      toast.error(message_code || LANGUAGE.Alert.Content4);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    let message_code;
    try {
      const response = await resendVerificationCode({ email, type: "REGISTER" });
      if (response.data.message === "Success_resend_001") message_code = LANGUAGE.Alert.Content5;
      toast.success(message_code);
    } catch (error) {
      const m = error.response?.data?.message;
      if (m === "Error_resend_001") message_code = LANGUAGE.Alert.Content6;
      if (m === "Error_resend_002") message_code = LANGUAGE.Alert.Content7;
      if (m === "Error_resend_003") message_code = LANGUAGE.Alert.Content8;
      if (m === "Error_rateLimit_002") message_code = LANGUAGE.Alert.Content8;
      toast.error(message_code || LANGUAGE.Alert.Content9);
    } finally {
      setLoading(false);
    }
  };

  // 與忘記密碼頁一致：bg #F6F6F6 / radius 6px / 內文 bodyM #000 / placeholder bodyM #C4C4C4
  const filledInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: COLORS.grayLightest,
      borderRadius: "6px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused": {
        backgroundColor: COLORS.white,
        "& fieldset": { border: "1px solid", borderColor: COLORS.main },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 8px 8px 8px",
      ...TYPO.bodyM,
      color: COLORS.black,
      "&::placeholder": { ...TYPO.bodyM, color: COLORS.grayMedian, opacity: 1 },
    },
  };

  const cardLogo =
    language === "zh-TW" || language === "zh-CN"
      ? "/images/logo/logo_2_1.png"
      : "/images/logo/logo_2_2.png";

  return (
    <SignInLayout>
      <Card
        sx={{
          width: "100%",
          maxWidth: 920,
          minHeight: { xs: "auto", md: 600 },
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Grid container sx={{ minHeight: "inherit" }}>
          {/* === 左欄：品牌（白底 + 右框線 + logo 162×254 置中）=== */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: COLORS.white,
              borderRight: `1px solid ${COLORS.grayLighter}`,
              px: 10,
              py: 8,
            }}
          >
            <MDBox component="img" src={cardLogo} alt="MASQuant" sx={{ width: 162, height: 254, objectFit: "contain" }} />
          </Grid>

          {/* === 右欄：表單 === */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              background: COLORS.white,
              p: { xs: 3, sm: 5, md: 8 },
            }}
          >
            <MDBox sx={{ width: "100%", maxWidth: 320 }}>
              {/* 手機版 logo */}
              <MDBox display={{ xs: "flex", md: "none" }} justifyContent="center" mb={3}>
                <MDBox component="img" src={cardLogo} alt="MASQuant" sx={{ maxHeight: 120, objectFit: "contain" }} />
              </MDBox>

              {/* 驗證 Email — displayM / #000 / 500 */}
              <MDTypography sx={{ ...TYPO.displayM, color: COLORS.black, fontWeight: 500, mb: 1 }}>
                {LANGUAGE.Main.Content1}
              </MDTypography>
              {/* 副標：請輸入寄送到 xxx@gmail.com 的驗證碼 — bodyM / grayDark / 400 */}
              <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.grayDark, fontWeight: 400, mb: 3 }}>
                {LANGUAGE.Main.Content2}{" "}
                <strong style={{ color: COLORS.black }}>{email}</strong>{" "}
                {LANGUAGE.Main.Content3}
              </MDTypography>

              <MDBox component="form" onSubmit={handleSubmit}>
                {/* 驗證碼* — bodyS / #000 / 400 */}
                <MDBox mb={2}>
                  <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 1 }}>
                    {LANGUAGE.Main.Content4}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    required
                    sx={filledInputSx}
                  />
                </MDBox>

                {/* 確認驗證 — 高 60 / radius 16 / 漸層 / title / #FFF / 500 */}
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={loading || !code}
                  sx={{
                    height: 60,
                    borderRadius: "16px",
                    background: COLORS.gradient,
                    boxShadow: "4px 4px 9px 0 rgba(222, 86, 15, 0.20)",
                    textTransform: "none",
                    ...TYPO.title,
                    color: `${COLORS.white} !important`,
                    fontWeight: 500,
                    "&:hover": {
                      background: COLORS.gradient,
                      boxShadow: "4px 4px 12px 0 rgba(222, 86, 15, 0.30)",
                    },
                    "&:disabled": {
                      background: COLORS.gradient,
                      color: `${COLORS.white} !important`,
                      opacity: 0.6,
                    },
                  }}
                >
                  {loading ? LANGUAGE.Alert.Content10 : LANGUAGE.Alert.Content11}
                </MDButton>

                {/* 分隔線 */}
                <Divider sx={{ my: 3, borderColor: COLORS.grayLighter }} />

                {/* 沒收到驗證碼嗎（bodyM/#000/400） + 重新發送驗證碼（refresh icon + title/#F36D26/500）*/}
                <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.black, fontWeight: 400 }}>
                    {LANGUAGE.Main.Content5}
                  </MDTypography>
                  <MDBox
                    component="a"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleResend();
                    }}
                    className="auth-underline"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                  >
                    <Icon sx={{ fontSize: "16px !important", color: COLORS.main }}>refresh</Icon>
                    <span style={{ ...TYPO.title, color: COLORS.main, fontWeight: 500 }}>{LANGUAGE.Main.Content6}</span>
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      {loading && (
        <div className="loading-overlay">
          <CircularProgress color="inherit" />
        </div>
      )}
    </SignInLayout>
  );
}
